import { Link, useLocation } from 'react-router-dom';
import { logout } from '../lib/auth';
import { MentorProvider, useMentorContext } from '../contexts/MentorContext';

/**
 * MentorLayout — shell layout for mentor role with scope-filtered navigation.
 *
 * Navigation items are filtered based on the mentor's granted scope:
 *   - Dashboard: always visible
 *   - Knowledge Base: visible if scope includes 'profile'
 *   - Documents: visible if scope includes 'cvs' or 'cover_letters'
 *   - Suggestions: always visible
 *
 * Applicant-only items (Pipeline, Starter Interview, LinkedIn, Mentors invite)
 * are never shown in this layout.
 *
 * If the mentor link status is not 'active', only Dashboard is shown
 * (inactive state messaging is handled by the dashboard page itself).
 *
 * Requirements: 1.2, 1.3, 6.5
 */

interface NavItem {
  path: string;
  label: string;
  icon: string;
  /** If defined, item is only shown when at least one of these scope values is present */
  requiredScope?: string[];
}

const mentorNavItems: NavItem[] = [
  { path: '/', label: 'Dashboard', icon: '📊' },
  { path: '/mentor/kb', label: 'Knowledge Base', icon: '📚', requiredScope: ['profile'] },
  { path: '/mentor/documents', label: 'Documents', icon: '📄', requiredScope: ['cvs', 'cover_letters'] },
  { path: '/mentor/suggestions', label: 'Suggestions', icon: '💡' },
];

function MentorLayoutInner({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const { dashboard, loading, error } = useMentorContext();

  // Determine which nav items to show based on scope
  const visibleNavItems = getVisibleNavItems(dashboard?.status, dashboard?.scope ?? []);

  return (
    <div className="min-h-screen flex flex-col">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-xl font-bold text-blue-600">Job App</span>
          <span className="text-sm text-gray-500">Mentor View</span>
        </div>
        <button
          onClick={logout}
          className="text-sm text-gray-600 hover:text-gray-900 px-3 py-1 rounded border border-gray-200 hover:border-gray-400 transition-colors"
          aria-label="Sign out"
        >
          Sign out
        </button>
      </header>

      <div className="flex flex-1">
        {/* Sidebar navigation */}
        <nav className="w-56 bg-white border-r border-gray-200 p-4" aria-label="Mentor navigation">
          {loading ? (
            <div className="flex items-center justify-center py-4" aria-label="Loading navigation">
              <div className="animate-pulse space-y-3 w-full">
                <div className="h-8 bg-gray-200 rounded" />
                <div className="h-8 bg-gray-200 rounded" />
                <div className="h-8 bg-gray-200 rounded" />
              </div>
            </div>
          ) : error ? (
            <p className="text-sm text-red-600 px-3 py-2">{error}</p>
          ) : (
            <ul className="space-y-1">
              {visibleNavItems.map((item) => {
                const isActive =
                  item.path === '/'
                    ? location.pathname === '/'
                    : location.pathname.startsWith(item.path);
                return (
                  <li key={item.path}>
                    <Link
                      to={item.path}
                      className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                        isActive
                          ? 'bg-blue-50 text-blue-700'
                          : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                      }`}
                      aria-current={isActive ? 'page' : undefined}
                    >
                      <span aria-hidden="true">{item.icon}</span>
                      {item.label}
                    </Link>
                  </li>
                );
              })}
            </ul>
          )}
        </nav>

        {/* Main content */}
        <main className="flex-1 p-6" role="main">
          {children}
        </main>
      </div>
    </div>
  );
}

/**
 * Determines visible navigation items based on link status and scope.
 *
 * - If status is not 'active', only Dashboard is shown (R2.4)
 * - Otherwise, items are filtered by requiredScope (R1.2, R6.5)
 */
function getVisibleNavItems(status: string | undefined, scope: string[]): NavItem[] {
  // While loading or if link is not active, show only Dashboard
  if (!status || status !== 'active') {
    return mentorNavItems.filter((item) => item.path === '/');
  }

  return mentorNavItems.filter((item) => {
    // Items without requiredScope are always visible
    if (!item.requiredScope) return true;
    // Item is visible if at least one of its requiredScope values is in the granted scope
    return item.requiredScope.some((s) => scope.includes(s));
  });
}

/**
 * MentorLayout wraps children with the MentorProvider so that
 * all mentor pages can access dashboard data (scope, status, etc.)
 * via useMentorContext().
 */
export default function MentorLayout({ children }: { children: React.ReactNode }) {
  return (
    <MentorProvider>
      <MentorLayoutInner>{children}</MentorLayoutInner>
    </MentorProvider>
  );
}

// Re-export for use by mentor pages
export { useMentorContext } from '../contexts/MentorContext';
